import { useEffect, useState } from 'react'
import { acceptAll, consentIsRequired, denyAll, setConsent, useConsent } from '@lb/domain'
import { useTranslation } from '@lb/i18n'
import { Button, cn } from '@lb/ui'

import { setAnalyticsAllowed } from '../../lib/telemetry'
import { useSessionContext } from '../providers/session'

/**
 * Keeps the telemetry gate in step with the stored choice.
 *
 * Separate from the banner because the banner unmounts once answered, and the
 * gate must keep being enforced after that — including when the member changes
 * their mind in Profile.
 */
export function ConsentGate() {
  const consent = useConsent()
  const allowed = consent.status === 'answered' && consent.choices.analytics

  useEffect(() => {
    setAnalyticsAllowed(allowed)
  }, [allowed])

  return null
}

/**
 * The Law 25 consent surface.
 *
 * ── Accept and Refuse are the same size, and that is the requirement ──────
 * Québec's Law 25 says withdrawing must be as easy as giving. A prominent
 * "Accept" beside a quiet "Manage preferences" is the pattern the law exists to
 * stop, and it is what most banners do. So the two primary actions are
 * rendered identically — same size, same shape, same prominence — and the only
 * de-emphasised control is the one that opens *more* choice, not less.
 *
 * ── Not a modal ──────────────────────────────────────────────────────────
 * It does not trap focus and does not block the page. A member who ignores it
 * can still read, and nothing non-essential runs while they do — `unknown` is
 * treated as refusal, so there is nothing to protect by holding them hostage.
 * A consent wall would also be its own dark pattern.
 *
 * ── Rendered outside the app shell ───────────────────────────────────────
 * It has to appear on the public funnel too. Consent is asked of a visitor, not
 * only of a member.
 */
export function ConsentBanner() {
  const { t } = useTranslation()
  const consent = useConsent()
  const { viewer } = useSessionContext()
  const [banner, setBanner] = useState<HTMLElement | null>(null)

  /*
   * Reserve the space the banner occupies, rather than floating over the page.
   *
   * A fixed strip at the bottom hides whatever is underneath it, and what is
   * underneath is routinely the primary action — the House Rules "J'accepte",
   * the last card in a grid, a form's submit. The E2E suite found this by
   * timing out on clicks that landed on the banner instead; a human would have
   * experienced it as a button that does nothing.
   *
   * Measured rather than hard-coded: the banner grows when it is expanded and
   * when the copy wraps, and a constant would be wrong in both cases.
   */
  useEffect(() => {
    if (!banner) return

    /*
     * A CSS variable the SCROLLING COLUMN reads — not padding on `<body>`.
     *
     * Padding the body pushed the whole layout, including the CSS grid holding
     * the `h-dvh` sticky sidebar, so the shell gained 181px of dead space and a
     * second scrollbar. Visible on Profile as a page that simply looked broken.
     *
     * The variable lets only `<main>` reserve the room, which is the element
     * the banner actually covers.
     */
    const apply = () => {
      document.documentElement.style.setProperty('--consent-h', `${banner.offsetHeight}px`)
    }
    apply()

    const observer = new ResizeObserver(apply)
    observer.observe(banner)
    return () => {
      observer.disconnect()
      document.documentElement.style.removeProperty('--consent-h')
    }
  }, [banner])
  const [expanded, setExpanded] = useState(false)
  const [analytics, setAnalytics] = useState(false)

  /*
   * Nothing in this build requires consent, so nothing is asked.
   *
   * See `CONSENT_REQUIRED` in the domain module: no cookies, every stored key
   * strictly necessary, and Vimeo loaded with `dnt=1`. A banner here would be
   * asking permission for something we do not do — which is not harmless. It
   * trains people to dismiss the one that will matter, and this one occupied a
   * fifth of the viewport on every page.
   */
  if (!consentIsRequired()) return null

  // Asked and answered. The way back is `ConsentPreferences`, in Profile.
  if (consent.status === 'answered') return null

  return (
    <aside
      /*
       * An `<aside>` — a named `complementary` landmark — and deliberately not
       * a dialog. It is not modal, and announcing it as one would imply a trap
       * that is not there. `aria-live` is absent for the same kind of reason:
       * it is present on load, so there is no change to announce.
       */
      ref={setBanner}
      aria-label={t('consent.title')}
      className={cn(
        'fixed inset-x-0 bottom-0 z-[80] border-t border-border bg-surface px-card py-4',
        'shadow-[0_-8px_28px_rgb(41_51_57/0.12)]',
        /*
         * Above the mobile tab bar, not on top of it.
         *
         * At `bottom-0` this covered the bottom navigation on every phone —
         * so a member's FIRST visit, which is exactly when the banner shows,
         * had no reachable navigation at all. Caught by the E2E suite, which
         * timed out clicking tabs that were underneath it; nothing else would
         * have found it, because the desktop layout has no tab bar.
         *
         * Only offset when the bar exists: it renders for a signed-in member
         * and below `md` only.
         */
        viewer && 'bottom-[calc(50px+env(safe-area-inset-bottom))] md:bottom-0',
      )}
    >
      <div className="mx-auto flex max-w-[860px] flex-col gap-3">
        <div>
          <h2 className="font-serif text-[18px] text-ink">{t('consent.title')}</h2>
          <p className="mt-1 max-w-[70ch] text-[14px] text-ink-soft">{t('consent.body')}</p>
        </div>

        {expanded ? (
          <div className="flex flex-col gap-2 rounded-[var(--r-sm)] border border-border bg-canvas p-card">
            {/*
             * Strictly necessary storage is named but has no toggle — it is
             * exempt, and offering a switch that cannot be turned off is worse
             * than explaining why there isn't one.
             */}
            <p className="text-[13px] text-muted">{t('consent.necessary')}</p>

            <label className="flex items-start gap-2.5 pt-1">
              <input
                type="checkbox"
                checked={analytics}
                onChange={(event) => setAnalytics(event.target.checked)}
                className="mt-0.5 h-4 w-4 accent-[var(--ink)]"
              />
              <span>
                <span className="block text-[14px] font-semibold text-ink">
                  {t('consent.analytics')}
                </span>
                <span className="block text-[13px] text-muted">{t('consent.analyticsWhy')}</span>
              </span>
            </label>
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          {expanded ? (
            <Button onClick={() => setConsent({ analytics })}>{t('consent.save')}</Button>
          ) : (
            <>
              {/*
                * The same variant on both, on purpose. If these two ever stop
                * matching, the banner stops being lawful.
                */}
              <Button onClick={acceptAll}>{t('consent.accept')}</Button>
              <Button onClick={denyAll}>{t('consent.refuse')}</Button>
              <button
                onClick={() => setExpanded(true)}
                className="min-h-[44px] px-3 text-[14px] text-ink-soft underline"
              >
                {t('consent.customise')}
              </button>
            </>
          )}
        </div>
      </div>
    </aside>
  )
}
